import { useRef, useCallback, useState } from 'react';
import type { UndoableCommand, UndoStackAPI } from '../types';

/** Максимальная глубина стека undo */
export const MAX_UNDO_DEPTH = 50;

/**
 * Hook для управления стеком undo/redo.
 * Использует useRef для стеков (избежание React batching issues при быстрых Ctrl+Z).
 * Возвращает стабильный UndoStackAPI.
 */
export function useUndoStack(): UndoStackAPI {
  const undoStackRef = useRef<UndoableCommand[]>([]);
  const redoStackRef = useRef<UndoableCommand[]>([]);

  // Trigger для ре-рендера UI (canUndo/canRedo/descriptions)
  const [, setRevision] = useState(0);
  const bump = useCallback(() => setRevision((r) => r + 1), []);

  const push = useCallback(
    (command: UndoableCommand) => {
      const stack = undoStackRef.current;
      if (stack.length >= MAX_UNDO_DEPTH) {
        stack.shift(); // Удаляем самую старую запись
      }
      stack.push(command);
      redoStackRef.current = []; // FR-003: новое действие очищает redo
      bump();
    },
    [bump],
  );

  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    const cmd = stack.pop();
    if (!cmd) return; // Пустой стек — no-op
    cmd.undo();
    redoStackRef.current.push(cmd);
    bump();
  }, [bump]);

  const redo = useCallback(() => {
    const stack = redoStackRef.current;
    const cmd = stack.pop();
    if (!cmd) return; // Пустой стек — no-op
    cmd.execute();
    undoStackRef.current.push(cmd);
    bump();
  }, [bump]);

  const clear = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    bump();
  }, [bump]);

  const dropByEntityId = useCallback(
    (entityId: string) => {
      // A command matches if it (or, for a batch, any of its children) created
      // this entity. Drop from both stacks so a server-rejected optimistic
      // create leaves no dangling undo/redo entry.
      const matches = (cmd: UndoableCommand): boolean =>
        cmd.entityId === entityId ||
        (cmd.type === 'batch' &&
          (cmd as { commands?: readonly UndoableCommand[] }).commands?.some(matches) === true);
      const before = undoStackRef.current.length + redoStackRef.current.length;
      undoStackRef.current = undoStackRef.current.filter((c) => !matches(c));
      redoStackRef.current = redoStackRef.current.filter((c) => !matches(c));
      if (undoStackRef.current.length + redoStackRef.current.length !== before) bump();
    },
    [bump],
  );

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  const undoDescription = canUndo
    ? undoStackRef.current[undoStackRef.current.length - 1].description
    : null;

  const redoDescription = canRedo
    ? redoStackRef.current[redoStackRef.current.length - 1].description
    : null;

  return {
    push,
    undo,
    redo,
    clear,
    dropByEntityId,
    canUndo,
    canRedo,
    undoDescription,
    redoDescription,
  };
}
